"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { motion, type Variants } from "framer-motion";
import Magnetic from "./ui/Magnetic";
import HeroBackground from "./ui/HeroBackground";

const words = ["Secure", "Modern", "Scalable", "Robust"];
type FlipPhase = "in" | "hold" | "out";

function FlipWord() {
  const [{ index, phase }, setFlipState] = useState<{ index: number; phase: FlipPhase }>({
    index: 0,
    phase: "in",
  });
  const chars = words[index].split("");

  useEffect(() => {
    if (phase === "in") {
      const t = setTimeout(() => {
        setFlipState((prev) => ({ ...prev, phase: "hold" }));
      }, 600 + words[index].length * 40 + 300);
      return () => clearTimeout(t);
    }
    if (phase === "hold") {
      const t = setTimeout(() => {
        setFlipState((prev) => ({ ...prev, phase: "out" }));
      }, 2000);
      return () => clearTimeout(t);
    }
    if (phase === "out") {
      const t = setTimeout(() => {
        setFlipState((prev) => ({
          index: (prev.index + 1) % words.length,
          phase: "in",
        }));
      }, 600 + words[index].length * 30);
      return () => clearTimeout(t);
    }
  }, [phase, index]);

  return (
    <div className="inline-flex text-indigo-400" aria-live="polite">
      {chars.map((char, i) => (
        <motion.span
          key={`${index}-${i}`}
          initial={{ opacity: 0, y: 15, filter: "blur(6px)" }}
          animate={
            phase === "out"
              ? { opacity: 0, y: -15, filter: "blur(6px)" }
              : { opacity: 1, y: 0, filter: "blur(0px)" }
          }
          transition={{ duration: 0.35, delay: i * 0.03, ease: "easeOut" }}
          className="inline-block"
        >
          {char === " " ? "\u00A0" : char}
        </motion.span>
      ))}
    </div>
  );
}

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 25, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const trustedTech = [
  { name: "Docker", iconClass: "fi fi-brands-docker" },
  { name: "AWS", src: "/aws-svgrepo-com.svg" },
  { name: "Cloudflare", src: "/logo_dark.svg" },
  { name: "Redis", src: "/redis-svgrepo-com.svg" },
  { name: "MySQL", iconClass: "fi fi-rr-database" },
  { name: "NGINX", iconClass: "fi fi-rr-settings" },
  { name: "Node.js", src: "/nodejs-svgrepo-com.svg" },
  { name: "Vue.js", iconClass: "fi fi-rr-computer" },
  { name: "React", src: "/logo_dark.svg" },
  { name: "Kubernetes", iconClass: "fi fi-rr-server" },
  { name: "Prisma", src: "/prisma-svgrepo-com.svg" },
  { name: "Tailwind", src: "/tailwindcss-icon-svgrepo-com.svg" }
];

export default function Hero() {
  const openTerminal = () => {
    const event = new CustomEvent("open-command-palette");
    window.dispatchEvent(event);
  };

  return (
    <section
      id="home"
      className="relative z-10 min-h-screen flex flex-col justify-between pt-28 pb-10 overflow-hidden"
    >
      <HeroBackground />

      <div className="max-w-7xl mx-auto px-6 w-full flex-1 flex items-center">
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center w-full">
          {/* Content side */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center md:text-left flex flex-col items-center md:items-start"
          >
            {/* Availability badge */}
            <motion.div variants={itemVariants}>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-wider mb-6">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Available for: Remote • Contract • Open Source • Speaking
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl md:text-6xl font-black text-white font-[var(--font-poppins)] leading-[0.94] tracking-tighter mb-4"
            >
              Designing Scalable <br />
              <span className="gradient-text">Digital Infrastructure.</span>
            </motion.h1>

            {/* Sub-badges */}
            <motion.div
              variants={itemVariants}
              className="text-indigo-300 font-mono text-xs font-bold tracking-widest uppercase mb-4"
            >
              Full Stack Engineer • System Architect • DevOps Engineer
            </motion.div>

            {/* Flip text */}
            <motion.div
              variants={itemVariants}
              className="text-3xl sm:text-4xl md:text-5xl font-black text-white/90 font-[var(--font-poppins)] leading-none min-h-[3rem] flex items-center gap-x-2"
            >
              <span>Developing</span> <FlipWord /> <span>Systems</span>
            </motion.div>

            {/* Main description */}
            <motion.p
              variants={itemVariants}
              className="text-gray-400 text-sm sm:text-base leading-relaxed mt-4 mb-8 max-w-md font-medium"
            >
              I build high-performance applications, cloud infrastructure, and developer-focused products that scale under immense workloads.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-4 items-center justify-center md:justify-start">
              <Magnetic>
                <a
                  href="#projects"
                  className="btn-gradient flex items-center gap-2.5 px-6 py-3.5 rounded-2xl font-bold shadow-2xl shadow-indigo-500/30 text-xs uppercase tracking-wider"
                >
                  <span>Inspect Masterpieces</span>
                  <i className="fi fi-rr-arrow-right text-xs" />
                </a>
              </Magnetic>

              <div className="flex items-center gap-2">
                <Magnetic>
                  <button
                    onClick={openTerminal}
                    className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-indigo-400 transition-all cursor-pointer"
                    title="Press / or click to open Command Palette CLI"
                  >
                    <i className="fi fi-rr-terminal text-base" />
                  </button>
                </Magnetic>
                <Magnetic>
                  <a
                    href="/profile.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass px-5 py-3.5 rounded-2xl border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-indigo-400 transition-all text-xs font-black uppercase tracking-widest font-mono"
                  >
                    Resume
                  </a>
                </Magnetic>
                <Magnetic>
                  <a
                    href="https://github.com/CodeByAmrit"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="GitHub Profile"
                    className="w-12 h-12 rounded-2xl glass border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-indigo-400 transition-all"
                  >
                    <i className="fi fi-brands-github text-base" />
                  </a>
                </Magnetic>
                <Magnetic>
                  <a
                    href="https://www.linkedin.com/in/amrit-sharma-b11b88124"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn Profile"
                    className="w-12 h-12 rounded-2xl glass border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-indigo-400 transition-all"
                  >
                    <i className="fi fi-brands-linkedin text-base" />
                  </a>
                </Magnetic>
              </div>
            </motion.div>
          </motion.div>

          {/* Profile Visual side */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex items-center justify-center"
          >
            <div className="absolute w-[120%] h-[120%] bg-indigo-500/10 blur-[120px] rounded-full pointer-events-none" />

            <div className="relative z-10 w-72 h-72 sm:w-80 sm:h-80 md:w-96 md:h-96 lg:w-[32rem] lg:h-[32rem]">
              <Image
                src="/amritnew.png"
                alt="Amrit Sharma - Senior Full Stack Developer and Software Architect"
                fill
                priority
                fetchPriority="high"
                loading="eager"
                sizes="(max-width: 768px) 280px, (max-width: 1200px) 384px, 512px"
                className="object-contain drop-shadow-[0_20px_50px_rgba(99,102,241,0.35)]"
                style={{
                  maskImage: "linear-gradient(to bottom, black 65%, transparent 98%)",
                  WebkitMaskImage: "linear-gradient(to bottom, black 65%, transparent 98%)",
                }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Trust marquee section at the bottom of hero */}
      <div className="w-full overflow-hidden border-t border-b border-white/5 bg-black/10 backdrop-blur-md py-6 mt-16 relative z-10">
        <div className="max-w-7xl mx-auto px-6 mb-3 text-center">
          <span className="text-[10px] font-mono font-bold tracking-[0.25em] text-gray-500 uppercase">
            Trusted Production Tooling Stack
          </span>
        </div>
        
        {/* Infinite scrolling marquee wrapper */}
        <div className="flex gap-16 animate-marquee whitespace-nowrap min-w-full">
          <div className="flex items-center gap-16 justify-around min-w-full shrink-0">
            {trustedTech.map((tech, idx) => (
              <div key={idx} className="flex items-center gap-2.5 text-xs text-gray-400 font-mono tracking-wider">
                {tech.src ? (
                  <Image src={tech.src} alt={tech.name} width={14} height={14} className="opacity-40 grayscale group-hover:grayscale-0 transition-all" />
                ) : (
                  <i className={`${tech.iconClass} opacity-40`} />
                )}
                <span>{tech.name}</span>
              </div>
            ))}
          </div>
          {/* Double list to loop seamlessly */}
          <div className="flex items-center gap-16 justify-around min-w-full shrink-0" aria-hidden="true">
            {trustedTech.map((tech, idx) => (
              <div key={`dup-${idx}`} className="flex items-center gap-2.5 text-xs text-gray-400 font-mono tracking-wider">
                {tech.src ? (
                  <Image src={tech.src} alt={tech.name} width={14} height={14} className="opacity-40 grayscale" />
                ) : (
                  <i className={`${tech.iconClass} opacity-40`} />
                )}
                <span>{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
